import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import api, { API } from "../lib/api";
import Logo from "../components/Logo";
import { ArrowRight, Users2, GitFork, Star, ShieldCheck, UserPlus, UserCheck, Sparkles } from "lucide-react";

/**
 * Creator Discovery — public page ranking users by public model count + forks + stars.
 * Anonymous visitors can browse but need to sign in to follow.
 */
export default function Creators() {
  const [creators, setCreators] = useState([]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [followingIds, setFollowingIds] = useState({}); // user_id -> bool
  const [busyId, setBusyId] = useState(null);

  const isAuthed = !!localStorage.getItem("token");

  useEffect(() => {
    axios.get(`${API}/creators?limit=40`, { withCredentials: false })
      .then((r) => setCreators(r.data.creators || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    // If authed, batch-load follow status in ONE call
    if (!isAuthed || creators.length === 0) return;
    (async () => {
      try {
        const ids = creators.map((c) => c.user_id);
        const r = await api.post("/follow/status", { user_ids: ids });
        setFollowingIds(r.data.status || {});
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line
  }, [creators.length, isAuthed]);

  const toggleFollow = async (creator) => {
    if (!isAuthed) return; // guarded by UI
    setBusyId(creator.user_id);
    const wasFollowing = !!followingIds[creator.user_id];
    // Optimistic bump
    setFollowingIds((f) => ({ ...f, [creator.user_id]: !wasFollowing }));
    setCreators((cs) => cs.map((c) => c.user_id === creator.user_id
      ? { ...c, followers: Math.max(0, c.followers + (wasFollowing ? -1 : 1)) }
      : c));
    try {
      if (wasFollowing) {
        await api.delete(`/follow/${creator.user_id}`);
      } else {
        await api.post(`/follow/${creator.user_id}`);
      }
    } catch (e) {
      // Revert optimistic changes
      setFollowingIds((f) => ({ ...f, [creator.user_id]: wasFollowing }));
      setCreators((cs) => cs.map((c) => c.user_id === creator.user_id
        ? { ...c, followers: Math.max(0, c.followers + (wasFollowing ? 1 : -1)) }
        : c));
      alert(e.response?.data?.detail || "Follow failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      <header className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <Link to="/"><Logo size={36} /></Link>
        <div className="flex items-center gap-3">
          <Link to="/showcase" className="text-sm font-heading font-bold text-slate-700 hover:text-slate-900 hidden sm:inline">Showcase</Link>
          <Link to="/models" className="text-sm font-heading font-bold text-slate-700 hover:text-slate-900 hidden sm:inline">Brains</Link>
          <Link to={isAuthed ? "/studio" : "/login"} className="brut-btn text-sm" data-testid="creators-cta-top">
            {isAuthed ? "Open Studio" : "Try AiMedley"} <ArrowRight size={12} strokeWidth={2.5} />
          </Link>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-6 pb-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-4">
          <Users2 size={12} strokeWidth={2.5} /> Creator Discovery
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-3" data-testid="creators-title">
          The people <span className="bg-amber-200 px-2 border-2 border-slate-900 inline-block rotate-[-1deg]">building</span> AiMedley
        </h1>
        <p className="text-base text-slate-600 max-w-2xl mx-auto">
          Ranked by their public models, forks earned, and stars collected. Follow anyone to get an inbox ping when they ship something new.
        </p>
      </section>

      <div className="max-w-6xl mx-auto px-6 pb-20">
        {err && <div className="brut-card p-4 text-red-600 text-sm">{err}</div>}
        {loading && <div className="italic text-slate-400 text-sm">Loading creators…</div>}
        {!loading && creators.length === 0 && (
          <div className="brut-card p-8 text-center">
            <div className="text-4xl mb-2">🌱</div>
            <div className="font-heading font-bold text-lg mb-1">No public creators yet</div>
            <p className="text-sm text-slate-600 mb-3">Ship a public model in Model Studio and you'll be the first here.</p>
            <Link to={isAuthed ? "/studio/new/composed" : "/login"} className="brut-btn text-sm inline-flex" data-testid="empty-cta">
              <Sparkles size={12} strokeWidth={3} /> Compose a model
            </Link>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="creators-grid">
          {creators.map((c) => (
            <CreatorCard
              key={c.user_id}
              c={c}
              isAuthed={isAuthed}
              isFollowing={!!followingIds[c.user_id]}
              busy={busyId === c.user_id}
              onFollow={() => toggleFollow(c)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CreatorCard({ c, isAuthed, isFollowing, busy, onFollow }) {
  return (
    <div
      className="brut-card p-5 flex flex-col gap-4 relative overflow-hidden"
      style={{ boxShadow: "4px 4px 0 #F59E0B" }}
      data-testid={`creator-card-${c.user_id}`}
    >
      <div className="flex items-start gap-3">
        <Link to={`/creators/${c.user_id}`} className="w-14 h-14 rounded-full border-2 border-slate-900 bg-amber-200 flex items-center justify-center font-heading font-bold text-xl shrink-0 hover:bg-amber-300 transition-colors" data-testid={`creator-avatar-${c.user_id}`}>
          {c.initials}
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/creators/${c.user_id}`} className="font-heading font-bold text-lg leading-tight hover:text-amber-700" data-testid={`creator-name-${c.user_id}`}>{c.name}</Link>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
            {c.followers} follower{c.followers === 1 ? "" : "s"}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-xs">
        <Stat icon={Sparkles}  label="Models" value={c.public_models} />
        <Stat icon={GitFork}   label="Forks"  value={c.total_forks} />
        <Stat icon={Star}      label="Stars"  value={c.total_stars} />
      </div>

      {c.top_models.length > 0 && (
        <div className="space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500">Top models</div>
          {c.top_models.map((m) => (
            <Link
              key={m.ai_model_id}
              to={`/studio/${m.ai_model_id}`}
              className="flex items-center gap-2 bg-slate-50 hover:bg-amber-50 border border-slate-200 rounded-lg px-2 py-1.5 transition-colors"
              data-testid={`top-model-${m.ai_model_id}`}
            >
              <span className="text-lg leading-none">{m.avatar}</span>
              <span className="flex-1 min-w-0 truncate font-heading font-bold text-sm">{m.name}</span>
              <span className="text-[10px] text-slate-500 font-mono inline-flex items-center gap-1">
                <GitFork size={9} /> {m.fork_count}
                <Star size={9} /> {m.star_count}
                {m.grade !== undefined && m.grade !== null && (
                  <span className="inline-flex items-center gap-0.5" style={{ color: m.grade >= 80 ? "#10B981" : m.grade >= 50 ? "#F59E0B" : "#EF4444" }}>
                    <ShieldCheck size={9} strokeWidth={3} /> {m.grade}
                  </span>
                )}
              </span>
            </Link>
          ))}
        </div>
      )}

      {isAuthed ? (
        <button
          onClick={onFollow}
          disabled={busy}
          className={`brut-btn text-sm mt-auto disabled:opacity-40 ${isFollowing ? "bg-slate-900 text-white" : ""}`}
          data-testid={`follow-${c.user_id}`}
        >
          {isFollowing ? <><UserCheck size={12} strokeWidth={3} /> Following</> : <><UserPlus size={12} strokeWidth={3} /> Follow</>}
        </button>
      ) : (
        <Link to="/login" className="brut-btn text-sm mt-auto bg-white text-slate-900" data-testid={`follow-signin-${c.user_id}`}>
          <UserPlus size={12} strokeWidth={3} /> Sign in to follow
        </Link>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border-2 border-slate-200 rounded-lg py-2 px-1 text-center">
      <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        <Icon size={9} strokeWidth={3} /> {label}
      </div>
      <div className="font-mono font-bold text-lg leading-tight">{value}</div>
    </div>
  );
}
